import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, BellRing, Check, X, ExternalLink, Clock } from "lucide-react";
import { notifications as api } from "../services/notifications";
import { on } from "../utils/notifyBus";

function useIsMobile() {
  const [v, setV] = useState(() => window.innerWidth <= 767);
  useEffect(() => {
    const h = () => setV(window.innerWidth <= 767);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return v;
}

const T = {
  dark: "#0c1118", amber: "#f9ab14", bg: "#f4f6f8",
  surface: "#ffffff", border: "#e8ecf0", muted: "#6b7280",
};

if (typeof document !== "undefined" && !document.getElementById("nb-styles")) {
  const s = document.createElement("style");
  s.id = "nb-styles";
  s.textContent = `
    @keyframes nb-slideDown  { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
    @keyframes nb-slideUp    { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
    @keyframes nb-sheetUp    { from { transform:translateY(100%); } to { transform:translateY(0); } }
    @keyframes nb-progress   { from { width:100%; } to { width:0%; } }
    .nb-bell-btn:hover { background: #f4f6f8 !important; }
    .nb-icon-btn:hover { background: #f4f6f8 !important; }
    .nb-row:hover { background: #f4f6f8 !important; }
    .nb-row:hover .nb-actions { opacity: 1 !important; }
    @media (max-width:767px) { .nb-actions { opacity: 1 !important; } }
  `;
  document.head.appendChild(s);
}

export default function NotificationBell({ admin }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [toast, setToast] = useState(null);
  const ref = useRef(null);

  async function refresh() {
    try {
      const { items, unreadCount } = await api.list({ onlyUnread: true, limit: 10 });
      setItems(items);
      setUnread(unreadCount);
    } catch {}
  }

  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    function onMessage(event) {
      const data = event.data;
      if (!data || data.type !== "PUSH_NOTIFICATION") return;
      setUnread((u) => u + 1);
      setToast({ title: data.payload?.title || "Notification", body: data.payload?.body || "" });
      setTimeout(() => setToast(null), 4000);
      refresh();
    }
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener("message", onMessage);
      return () => navigator.serviceWorker.removeEventListener("message", onMessage);
    }
  }, []);

  useEffect(() => {
    const off = on("notifications:changed", (ev) => {
      const delta = Number(ev?.detail?.delta || 0);
      if (delta) setUnread((u) => Math.max(0, u + delta));
      refresh();
    });
    return off;
  }, []);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    // On mobile the portal renders outside ref — skip this listener;
    // the sheet overlay's own onClick handles closing instead.
    if (open && !isMobile) {
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }
  }, [open, isMobile]);

  async function markAll() {
    await api.markAllRead();
    setUnread(0); setItems([]); setOpen(false);
    window.dispatchEvent(new CustomEvent("notifications:changed", { detail: { type: "markAllRead", delta: 0 } }));
  }

  async function markOne(id) {
    await api.markRead(id);
    setItems(items.filter((i) => i.id !== id));
    setUnread((u) => Math.max(0, u - 1));
    window.dispatchEvent(new CustomEvent("notifications:changed", { detail: { type: "markRead", id, delta: -1 } }));
  }

  const fmtTime = (d) => {
    const diff = Date.now() - new Date(d);
    const m = Math.floor(diff / 60000), h = Math.floor(m / 60), day = Math.floor(h / 24);
    if (m < 1) return "Just now";
    if (m < 60) return `${m}m ago`;
    if (h < 24) return `${h}h ago`;
    if (day < 7) return `${day}d ago`;
    return new Date(d).toLocaleDateString();
  };

  return (
    <div style={{ position: "relative", fontFamily: "'DM Sans', sans-serif" }} ref={ref}>
      {/* Bell Button */}
      <button
        className="nb-bell-btn"
        onClick={() => setOpen(!open)}
        aria-label="Notifications"
        style={{ position: "relative", padding: "8px 10px", background: "none", border: "none", borderRadius: 8, cursor: "pointer", color: T.muted, transform: open ? "scale(0.95)" : "scale(1)", transition: "transform 0.1s" }}
      >
        {unread > 0 ? <BellRing size={22} /> : <Bell size={22} />}
        {unread > 0 && (
          <div style={{ position: "absolute", top: -2, right: -2, width: 18, height: 18, background: "#ef4444", color: "#fff", fontSize: 10, fontWeight: 700, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {unread > 9 ? "9+" : unread}
          </div>
        )}
      </button>

      {/* Dropdown / Bottom Sheet */}
      {open && (isMobile ? createPortal(
        <div style={{ position: "fixed", inset: 0, zIndex: 9000, fontFamily: "'DM Sans', sans-serif" }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)" }} onClick={() => setOpen(false)} />
          <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, background: T.surface, borderRadius: "16px 16px 0 0", maxHeight: "80vh", display: "flex", flexDirection: "column", animation: "nb-sheetUp 0.28s cubic-bezier(.4,0,.2,1)" }}>
            {/* Handle */}
            <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px" }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: T.border }} />
            </div>
            <NbPanel items={items} unread={unread} admin={admin} markAll={markAll} markOne={markOne} fmtTime={fmtTime} onClose={() => setOpen(false)} />
          </div>
        </div>,
        document.body
      ) : (
        <div style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", width: 384, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: "0 8px 40px rgba(0,0,0,0.14)", zIndex: 50, overflow: "hidden", animation: "nb-slideDown 0.2s ease-out" }}>
          <NbPanel items={items} unread={unread} admin={admin} markAll={markAll} markOne={markOne} fmtTime={fmtTime} onClose={() => setOpen(false)} />
        </div>
      ))}

      {/* Push Toast */}

      {toast && (
        <div style={{ position: "fixed", bottom: 24, right: 16, left: 16, maxWidth: 380, margin: "0 auto", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: "0 8px 30px rgba(0,0,0,0.15)", padding: 16, zIndex: 9999, animation: "nb-slideUp 0.3s ease-out" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{ padding: 8, background: `${T.amber}22`, borderRadius: 8, flexShrink: 0 }}>
              <BellRing size={18} color={T.amber} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{toast.title}</div>
              <div style={{ fontSize: 13, color: T.muted }}>{toast.body}</div>
            </div>
            <button className="nb-icon-btn" onClick={() => setToast(null)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, borderRadius: 6 }}>
              <X size={14} color={T.muted} />
            </button>
          </div>
          <div style={{ marginTop: 10, height: 3, background: T.border, borderRadius: 2, overflow: "hidden" }}>
            <div style={{ height: "100%", background: T.amber, borderRadius: 2, animation: "nb-progress 4s linear" }} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Shared panel content (dropdown + bottom sheet) ──────────── */
function NbPanel({ items, unread, admin, markAll, markOne, fmtTime, onClose }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", overflow: "hidden", flex: 1 }}>
      {/* Header */}
      <div style={{ padding: "14px 20px", background: T.bg, borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <BellRing size={18} color={T.dark} />
            <span style={{ fontWeight: 700, fontSize: 16 }}>Notifications</span>
          </div>
          <button className="nb-icon-btn" onClick={onClose} style={{ background: "none", border: "none", borderRadius: 6, cursor: "pointer", padding: 4 }}>
            <X size={16} color={T.muted} />
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 13, color: T.muted }}>{unread > 0 ? `${unread} unread` : "All caught up!"}</span>
          {unread > 0 && (
            <button onClick={markAll} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 500, color: T.muted, background: "none", border: "none", cursor: "pointer", padding: "2px 8px", borderRadius: 6 }}>
              <Check size={12} /> Mark all read
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: "auto", maxHeight: 480 }}>
        {items.length === 0 ? (
          <div style={{ padding: "48px 20px", textAlign: "center" }}>
            <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 56, height: 56, background: T.bg, borderRadius: "50%", marginBottom: 12 }}>
              <Bell size={24} color={T.muted} />
            </div>
            <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>No unread notifications</p>
            <p style={{ fontSize: 12, color: T.muted }}>You're all caught up!</p>
          </div>
        ) : (
          items.map((n) => (
            <div key={n.id} className="nb-row" style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}`, position: "relative" }}>
              <div style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", width: 7, height: 7, background: T.amber, borderRadius: "50%" }} />
              <a href={n.linkUrl || (admin ? "/admin" : "/dashboard")} style={{ display: "block", marginBottom: 8, textDecoration: "none" }} onClick={onClose}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.dark }}>{n.title}</span>
                  {n.createdAt && (
                    <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.muted, whiteSpace: "nowrap" }}>
                      <Clock size={10} />{fmtTime(n.createdAt)}
                    </span>
                  )}
                </div>
                <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", margin: 0 }}>{n.body}</p>
              </a>
              <div className="nb-actions" style={{ display: "flex", gap: 8, opacity: 0, transition: "opacity 0.15s" }}>
                <button onClick={(e) => { e.stopPropagation(); markOne(n.id); }} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 500, color: "#16a34a", background: "none", border: "none", cursor: "pointer", padding: "2px 6px", borderRadius: 6 }}>
                  <Check size={12} /> Mark read
                </button>
                {n.linkUrl && (
                  <a href={n.linkUrl} onClick={onClose} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 500, color: T.amber, textDecoration: "none", padding: "2px 6px", borderRadius: 6 }}>
                    <ExternalLink size={12} /> Open
                  </a>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      {items.length > 0 && (
        <div style={{ padding: "10px 20px", background: T.bg, borderTop: `1px solid ${T.border}`, textAlign: "center", flexShrink: 0 }}>
          <a href={admin ? "/admin/notifications" : "/dashboard/notifications"} onClick={onClose} style={{ fontSize: 13, fontWeight: 500, color: T.dark, textDecoration: "none" }}>
            See all notifications
          </a>
        </div>
      )}
    </div>
  );
}
